import Bowser from 'bowser';
import { DELETE, INSERT } from 'fast-diff';
import StateMachine from 'javascript-state-machine';
import triggers from '~/actions/triggers';
import RS from '~/api';
import {
  KEY_LIST_OF_SCENARIOS_STORAGE,
  KEY_SCENARIOS_STORAGE, KEY_SETTINGS_ENABLED_ON_TABS, KEY_SETTINGS_ENABLED_ON_TABS_TIMEOUT,
  KEY_SETTINGS_STORAGE,
  KEY_SHOWN_SCENARIOS
} from '~/constants';
import generateUUID from '~/helpers/generateUUID';
import pathToString from '~/helpers/pathToString';
import safeCompare from '~/helpers/safeCompare';
import {
  deleteFromLocalStorage, deleteFromSessionStorage,
  getFromLocalStorage,
  getFromSessionStorage,
  saveToLocalStorage,
  saveToSessionStorage
} from '~/helpers/storage';
import { clickstreamLogger } from '~/logging';
import { findMatchingRulesForTree, isMatchingCondition } from '~/utils/NodeMatcher';
import settings from '~/utils/Settings';
import { getEnabledOnTabs, tabsTimeoutIsExpired, widgetsHasChanges } from '~/utils/notificationsOnTabsUtils';

const isNewUser = !window.localStorage.getItem('__csa-uuid');
if (isNewUser) window.localStorage.setItem('__csa-uuid', generateUUID());

const { platform } = Bowser.parse(window.navigator.userAgent);

const scenarios = [];
let isActive = false;

function prepareEvent(event) {
  const eventData = {
    ...event,
    target: undefined,
    processedTarget: undefined,
  };
  if (event.target) {
    eventData.pathToTarget = pathToString(event.target);
    if (event.processedTarget) {
      eventData.target = event.processedTarget;
    } else {
      const matching = findMatchingRulesForTree(
        event.target,
        settings.getOrDefault('ELEMENT_MATCHING_RULES', {})
      );
      eventData.target = matching.length
        ? matching.join(', ')
        : eventData.pathToTarget;
    }
  }

  return eventData;
}

function handleScenarioFinished(scenario, event) {
  const triggerName = scenario.trigger.name;

  if (scenario.trigger.configuration.postMessage) {
    const message = {
      type: 'SCENARIO_FINISHED',
      scenarioCode: scenario.code,
    };

    window.postMessage(message, location.origin);

    clickstreamLogger.debug('Scenario finished message was sent', message);
  }

  let triggerPromise = Promise.resolve('NOT_EXECUTED');
  if (typeof triggers[triggerName] === 'function') {
    triggerPromise = triggers[triggerName](
      scenario.name,
      scenario.trigger.configuration,
      scenario.trigger.extraHTML ? scenario.trigger.extraHTML : undefined
    );
  }
  triggerPromise.then((result) => {
    const targetToPayload = prepareEvent(event);

    processUserNotificationWasShown(triggerName, scenario.code, scenario);

    const eventParameters = [
      { SCENARIO_CODE: scenario.code },
      { SCENARIO_TRIGGER_RESULT: result ? result : 'OK' },
      { SESSION_ID: RS.token.sessionId },
    ];

    if (event.payload) {
      eventParameters.push({
        PAYLOAD: JSON.stringify({
          ...event.payload,
          name: targetToPayload.target,
        }),
      });
    }

    RS.scenarioEvent.set('SCENARIO_FINISHED', eventParameters);
  });
  if (scenario.repeat === true) {
    try {
      scenario.reset(event);
      saveToSessionStorage(KEY_SCENARIOS_STORAGE, scenario.code, {
        step: scenario.state,
      });
    } catch (e) {
      saveToSessionStorage(KEY_SCENARIOS_STORAGE, scenario.code, {
        step: `${scenario.state - 1}`,
        transitionTime: (new Date).getTime(),
      });
    }

  }
  if (scenario.newUsersOnly === true && !isNewUser) {
    return;
  }
  if (scenario.disableScenarios) {
    clickstreamLogger.debug(
      'Disabling scenarios: ',
      scenario.disableScenarios
    );
    markScenariosDisabled(scenario.disableScenarios);
  }
}

function processUserNotificationWasShown(triggerName, code, scenario) {

  if (triggerName === 'userNotification') {
    const enabledOnTabs = getEnabledOnTabs();
    const isScenarioShown = getFromLocalStorage(KEY_SHOWN_SCENARIOS, code);

    if (!enabledOnTabs && !isScenarioShown) {
      const uuid = scenario?.trigger?.configuration?.uuid;
      const time = Date.now();

      saveToLocalStorage(KEY_SHOWN_SCENARIOS, code, {
        code,
        uuid,
        time,
      });
    }
  }
}

function markScenariosDisabled(disableScenarios) {
  for (let i = 0; i < disableScenarios.length; i++) {
    for (let j = 0; j < scenarios.length; j++) {
      if (scenarios[j].code === disableScenarios[i]) {
        const scenario = scenarios[j];
        scenario.disabled = true;
        saveToSessionStorage(KEY_SCENARIOS_STORAGE, scenario.code, {
          step: scenario.state,
          disabled: true,
        });
        break;
      }
    }
  }
}

function onTransition(lifecycle, event) {
  if (event) {
    const eventData = { ...event };
    if (event.target) {
      const matching = findMatchingRulesForTree(
        event.target,
        settings.getOrDefault('ELEMENT_MATCHING_RULES', {})
      );
      eventData.target = matching.length
        ? matching.join(', ')
        : pathToString(event.target);
    }
    this.eventsHistory[lifecycle.from] = eventData;
  }
}

// проверить, что текущее время клиента внутри временного интервала
function checkTimeFrame(rule) {
  const from = new Date(rule.check.dateInterval.from * 1000);
  const to = new Date(rule.check.dateInterval.to * 1000);
  const now = new Date();

  return from <= now && to >= now;
}

function isTransitionPossible(event) {
  const scenario = this;
  const rules = this.transitionRules[this.state];
  let lastCheckedRule = null;
  return {
    possible: rules
      ?.sort((a, b) => (a.cancel ? 0 : 1) - (b.cancel ? 0 : 1))
      .some((rule) => {
        lastCheckedRule = rule;

        return Object.keys(rule.check).every((property) => {
          if (property === 'target') {
            return isMatchingCondition(event.target, rule.check.target);
          } else if (property === 'payload') {
            return isMatchingCondition(event.payload, rule.check.payload);
          } else if (property === 'child') {
            return (
              event.nodes &&
              event.nodes.some((node) => {
                return isMatchingCondition(node, rule.check.child);
              })
            );
          } else if (property === 'map') {
            return (
              event.maps &&
              [...event.maps].some((map) => {
                return isMatchingCondition(map, rule.check.map);
              })
            );
          } else if (property === 'difference') {
            if (!event.difference || !Array.isArray(event.difference)) {
              return false;
            }

            return event.difference.some((part) => {
              return (
                (rule.check.difference === 'delete' && part[0] === DELETE) ||
                (rule.check.difference === 'insert' && part[0] === INSERT)
              );
            });
          } else if (property === 'attribute') {
            const ruleAttributeName = rule.check.attribute.name;
            const attributeName = event.attributeName;

            if (ruleAttributeName === 'style') {
              const [rulePropertyName, rulePropertyValue] =
                rule.check.attribute.value.split(':');
              const propertyValue = event.target.style[rulePropertyName];

              return (
                attributeName === 'style' &&
                safeCompare(rulePropertyValue, propertyValue)
              );
            } else {
              const [ruleAttributeValue, attributeValue] = [
                rule.check.attribute.value,
                event.target.getAttribute(attributeName),
              ];

              return safeCompare(ruleAttributeValue, attributeValue);
            }
          } else if (property === 'forms') {
            if (!event.forms) return false;

            for (const formAndRulesObject of rule.check.forms) {
              let formToCheck = null;
              for (const form of event.forms) {
                if (isMatchingCondition(form, formAndRulesObject.form)) {
                  formToCheck = form;
                  break;
                }
              }

              if (!formToCheck) {
                return false;
              }

              const fieldsToCheck = [...(formToCheck.elements || [])].filter(
                (element) => {
                  return (
                    element.tagName &&
                    element.tagName.toLowerCase() !== 'button' &&
                    element.type.toLowerCase() !== 'checkbox' &&
                    element.type.toLowerCase() !== 'radio'
                  );
                }
              );

              for (const fieldToCheck of fieldsToCheck) {
                if (
                  !isMatchingCondition(
                    fieldToCheck,
                    getFieldRules(fieldToCheck, formAndRulesObject)
                  )
                )
                  return false;
              }
            }

            return true;
          } else if (property === 'time') {
            const valueToCheck =
              rule.check.type === 'time'
                ? new Date().getTime() - scenario.transitionTime
                : event.time;
            if (rule.check[property].startsWith('>')) {
              return (
                valueToCheck >
                Number(rule.check[property].replace('>', '').trim())
              );
            } else if (rule.check[property].startsWith('<')) {
              return (
                valueToCheck <
                Number(rule.check[property].replace('<', '').trim())
              );
            } else {
              return valueToCheck === Number(rule.check[property]);
            }
          } else if (property === 'deviceLimitation') {
            return rule.check[property].includes(platform.type);
          } else if (property === 'newUsersOnly') {

            const noValueInStore = !getFromSessionStorage(KEY_SCENARIOS_STORAGE, scenario.code);
            return (isNewUser === true && rule.check[property] === isNewUser) || noValueInStore;
          } else if (property === 'dateInterval') {
            return checkTimeFrame(rule);
          } else {
            return safeCompare(rule.check[property], event[property]);
          }
        });
      }),
    audit: lastCheckedRule?.audit,
    cancel: lastCheckedRule?.cancel,
  };
}

function getFieldRules(field, formRulesData) {
  const rules = Object.assign({}, formRulesData['default-rules']);
  for (const fieldRulesData of formRulesData['field-rules'] || []) {
    if (isMatchingCondition(field, fieldRulesData.field)) {
      Object.assign(rules, fieldRulesData.rules);
    }
  }

  return rules;
}

const checkOnNotificationWasShown = code => getFromLocalStorage(KEY_SHOWN_SCENARIOS, code);

function generateScenarioStateMachine(code, configuration) {
  const transitions = [];
  const transitionRules = {};
  if (!configuration || !configuration.steps || !configuration.trigger) {
    clickstreamLogger.error(
      `Configuration of scenario [${code}] not configuration, or not steps, or not trigger!`,
      configuration
    );

    return;
  }
  if (!configuration.trigger.name || !configuration.trigger.configuration) {
    clickstreamLogger.error(
      'Scenario trigger configuration is not valid ',
      configuration.trigger
    );

    return;
  }

  /**
   * Allows to run scenario only on specified device types.
   * Should be an array of values ('mobile', 'desktop', 'tablet')
   */
  if (
    configuration.deviceLimitation &&
    !configuration.deviceLimitation.includes(platform.type)
  ) {
    return;
  }

  // нужно повторить сценарий
  const repeatScenario = !getEnabledOnTabs() && tabsTimeoutIsExpired(code);
  // Если таймаут просрочен, тогда очищаем локальное хранилище и откатываем показ виджета.
  if (repeatScenario) {
    try {
      deleteFromLocalStorage(KEY_SHOWN_SCENARIOS, code);
    } catch (e) {
      clickstreamLogger.error(e);
    }
  }

  const isNotificationWasShown = !!checkOnNotificationWasShown(code);

  if (isNotificationWasShown) return;

  const trigger = {
    name: configuration.trigger.name ? configuration.trigger.name : 'unknown',
    configuration: configuration.trigger.configuration
      ? configuration.trigger.configuration
      : {},
    extraHTML: configuration.trigger.extraHTML ? configuration.trigger.extraHTML : {},
  };

  const length = configuration.steps.length;
  const lastState = configuration.steps[length - 1].to;

  for (let i = 0; i < length; i++) {
    let { from, to, check, audit, cancel, goToStepAfterCancel } =
      configuration.steps[i];
    from = typeof from === 'number' ? `${from}` : from;
    to = typeof to === 'number' ? `${to}` : to;
    check = typeof check === 'string' ? JSON.parse(check) : check;
    transitions.push({
      name: cancel ? 'cancel' : 'step',
      from,
      to: cancel ? (goToStepAfterCancel ? to : '1') : to,
    });
    if (typeof transitionRules[from] === 'undefined') {
      transitionRules[from] = [];
    }
    transitionRules[from].push({ check, audit, cancel });
  }

  transitions.push({
    name: 'reset',
    from: typeof lastState === 'number' ? `${lastState}` : lastState,
    to: '1',
  });

  const scenarioInfo = getFromSessionStorage(KEY_SCENARIOS_STORAGE, code);

  scenarios.push(
    new StateMachine({
      init: scenarioInfo && scenarioInfo.step ? scenarioInfo.step : '1',
      transitions,
      data: {
        name: configuration.name,
        code,
        eventsHistory: {},
        transitionRules,
        trigger,
        repeat: configuration.repeat || repeatScenario,
        disabled: scenarioInfo && scenarioInfo.disabled,
        disableScenarios: configuration.disableScenarios,
        transitionTime:
          scenarioInfo && scenarioInfo.transitionTime
            ? scenarioInfo.transitionTime
            : new Date().getTime(),
      },
      methods: {
        onTransition,
        isTransitionPossible,
      },
    })
  );
}

class ScenariosTrackingService {
  get scenariosTrackingRules() {
    return settings.getOrDefault('SCENARIOS_TRACKING_RULES', {});
  }

  start() {
    if (isActive === false) {
      const scenariosFromSession = getFromSessionStorage(
        KEY_LIST_OF_SCENARIOS_STORAGE,
        'scenarios'
      );
      isActive = true;
      scenarios.length = 0;
      if (scenariosFromSession) {
        scenariosFromSession.map((scenario) => {
          const code = `${scenario.name.toUpperCase()}_${scenario.id}`;
          scenario.name = `${scenario.name}_${scenario.id}`;
          generateScenarioStateMachine(code, scenario);
        });
      }
    }
  }

  stop() {
    if (isActive) {
      isActive = false;
    }
  }

  check(event) {
    if (isActive) {
      scenarios.forEach((scenario) => {
        const actionAvailable = scenario.can('step') || scenario.can('cancel');
        const transitionPossible =
          scenario.isTransitionPossible(event).possible;

        const isScenarioTransitionable =
          !scenario.disabled && actionAvailable && transitionPossible;

        if (isScenarioTransitionable) {
          const stateFrom = scenario.state;
          if (scenario.isTransitionPossible(event).cancel === true) {
            scenario.cancel(event);
            clickstreamLogger.debug(
              `Event triggered cancel scenario ${scenario.code} transition from: ${stateFrom} to ${scenario.state}: `,
              event,
              scenario
            );
          } else {
            scenario.step(event);
            clickstreamLogger.debug(
              `Event triggered scenario ${scenario.code} transition from: ${stateFrom} to ${scenario.state}: `,
              event,
              scenario
            );
          }
          saveToSessionStorage(KEY_SCENARIOS_STORAGE, scenario.code, {
            step: scenario.state,
            transitionTime: new Date().getTime(),
          });
          if (scenario.cannot('step')) {
            handleScenarioFinished(scenario, event);
          } else if (scenario.isTransitionPossible(event).audit) {
            const targetToPayload = prepareEvent(event);

            RS.scenarioEvent.set('SCENARIO_TRANSITION', [
              { SCENARIO_CODE: scenario.code },
              { STATE_FROM: stateFrom },
              { STATE_TO: scenario.state },
              {
                PAYLOAD: event.payload
                  ? JSON.stringify({
                    ...event.payload,
                    name: targetToPayload.target,
                  })
                  : '{}',
              },
            ]);
          }
        }
      });
    }
  }
}

export default new ScenariosTrackingService();
